import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import {Tab, Tabs} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {PermissionsComponent} from "../Permissions/Permissions.Component";
import "./Tabs.Style.scss";
import {getDataFromObject, getIsAllowedPermissionV2} from "../../Helpers";

export const TabsComponent = memo(
  ({
    iconInput,
    data,
    currentTab,
    onTabChanged,
    wrapperClasses,
    tabsAriaLabel,
    labelInput,
    translationPath,
    parentTranslationPath,
    variant,
    orientation,
    iconOnly,
    themeClasses,
    scrollButtons,
    hiddenTabIndexes,
    maxIndex,
    minIndex,
    dynamicComponentProps,
    componentInput,
    withDynamicComponents,
    totalCountInput,
    isDisabled,
    isWithLine,
    isPrimary,
    idRef,
    dynamicIdRef,
    tabsContentRefInput,
    permissionsReducerInput,
    permissions,
    tabsContentClasses,
    tabsHeaderClasses,
    isHiddenByPermissions,
    tabIndicatorProps,
    getBeforeTabsComponent,
    getAfterTabsComponent,
    customLabel,
  }) => {
    const {t} = useTranslation(parentTranslationPath);
    const tabsContentRef = useRef(null);
    // const loginResponse = useSelector((state) => state.login.loginResponse);
    const permissionsReducer = useSelector((state) =>
      getDataFromObject(state, permissionsReducerInput, true)
    );

    const getTheFirstActiveTab = useCallback(() => {
      const firstActiveTabIndex = data.findIndex((item) =>
        getIsAllowedPermissionV2({
          defaultPermissions: item.defaultPermissions,
          permissions: permissions || permissionsReducer,
          permissionId: item.permissionId,
          allowEmptyRules: true,
        })
      );
      if (
        firstActiveTabIndex > currentTab ||
        (firstActiveTabIndex === -1 && currentTab !== -1)
      ) {
        if (onTabChanged) onTabChanged({}, firstActiveTabIndex);
      } else if (firstActiveTabIndex !== -1 && currentTab !== -1) {
        const checkCurrentTabAllow = getIsAllowedPermissionV2({
          defaultPermissions: data[currentTab]?.defaultPermissions,
          permissions: permissions || permissionsReducer,
          permissionId: data[currentTab]?.permissionId,
          allowEmptyRules: true,
        });
        if (!checkCurrentTabAllow && onTabChanged)
          onTabChanged({}, firstActiveTabIndex);
      }
    }, [currentTab, data, onTabChanged, permissions, permissionsReducer]);

    // this is to make sure that the active index is not disabled by permissions
    useEffect(() => {
      if (!isHiddenByPermissions && data) getTheFirstActiveTab();
    }, [isHiddenByPermissions, data, getTheFirstActiveTab]);

    return (
      <>
        <div
          className={`tabs-header-wrapper${
            (tabsHeaderClasses && `${tabsHeaderClasses}`) || ""
          }${(orientation === "vertical" && " is-vertical") || ""}`}
        >
          {getBeforeTabsComponent && getBeforeTabsComponent()}
          <Tabs
            value={currentTab !== -1 ? currentTab : false}
            onChange={onTabChanged}
            variant={variant}
            orientation={orientation}
            scrollButtons={scrollButtons}
            indicatorColor="primary"
            disabled={isDisabled}
            TabIndicatorProps={tabIndicatorProps}
            textColor="primary"
            className={`tabs-wrapper ${wrapperClasses} ${themeClasses}${
              isWithLine ? " is-with-line" : ""
            }${iconOnly ? " icon-only" : ""}${isPrimary ? " is-primary" : ""}`}
            aria-label={tabsAriaLabel}
          >
            {data &&
              data
                .filter(
                  (item, index) =>
                    hiddenTabIndexes.findIndex(
                      (element) => element === index
                    ) === -1 &&
                    (!isHiddenByPermissions ||
                      getIsAllowedPermissionV2({
                        defaultPermissions: item.defaultPermissions,
                        permissions: permissions || permissionsReducer,
                        permissionId: item.permissionId,
                        allowEmptyRules: true,
                      }))
                )
                .map((item, index) => (
                  <Tab
                    key={`${idRef}${index + 1}`}
                    disabled={
                      isDisabled ||
                      item.disabled ||
                      (!isHiddenByPermissions &&
                        !getIsAllowedPermissionV2({
                          defaultPermissions: item.defaultPermissions,
                          permissions: permissions || permissionsReducer,
                          permissionId: item.permissionId,
                          allowEmptyRules: true,
                        })) ||
                      index > (maxIndex || data.length - 1) ||
                      index < (minIndex || 0)
                    }
                    label={
                      labelInput &&
                      !iconOnly && (
                        <span className='my-2'>
                          {item.inlineIcon && (
                            <span className="mx-2">{item.inlineIcon}</span>
                          )}
                          {t(
                            `${translationPath}${
                              item?.isWithCustomLabel
                                ? customLabel
                                : item[labelInput]
                            }`
                          )}
                          {totalCountInput && (
                            <span className="px-1">
                              ({item[totalCountInput]})
                            </span>
                          )}
                        </span>
                      )
                    }
                    icon={
                      <span
                        className={(iconInput && item[iconInput]) || undefined}
                      />
                    }
                    onClick={item.onClick}
                    onContextMenu={item.onContextMenu}
                  />
                ))}
          </Tabs>
          {getAfterTabsComponent && getAfterTabsComponent()}
        </div>
        {(dynamicComponentProps || withDynamicComponents) && data && (
          <div
            className={`tabs-content-wrapper${
              (orientation === "vertical" && " is-vertical-tabs") || ""
            }${(tabsContentClasses && ` ${tabsContentClasses}`) || ""}`}
            ref={tabsContentRef}
          >
            {data
              .filter(
                (item, index) =>
                  hiddenTabIndexes.findIndex((element) => element === index) ===
                    -1 &&
                  (!isHiddenByPermissions ||
                    getIsAllowedPermissionV2({
                      defaultPermissions: item.defaultPermissions,
                      permissions: item.permissions || permissionsReducer,
                      permissionId: item.permissionId,
                      allowEmptyRules: true,
                    }))
              )
              .map((item, index) => {
                const Component = item[componentInput];

                return (
                  currentTab === index &&
                  ((Component && (
                    <PermissionsComponent
                      permissionsList={item.permissionsList}
                      permissionsId={item.permissionsId}
                      key={`${idRef}${dynamicIdRef}${index + 1}`}
                      allowEmptyRoles
                    >
                      <Component
                        {...((tabsContentRefInput && {
                          [tabsContentRefInput]: tabsContentRef,
                        }) ||
                          {})}
                        {...(dynamicComponentProps || {})}
                        {...(item.props || {})}
                      />
                    </PermissionsComponent>
                  )) ||
                    null)
                );
              })}
          </div>
        )}
      </>
    );
  }
);

TabsComponent.displayName = "TabsComponent";

TabsComponent.propTypes = {
  data: PropTypes.instanceOf(Array).isRequired,
  onTabChanged: PropTypes.func.isRequired,
  hiddenTabIndexes: PropTypes.arrayOf(PropTypes.number),
  currentTab: PropTypes.number,
  iconInput: PropTypes.string,
  labelInput: PropTypes.string,
  wrapperClasses: PropTypes.string,
  tabsHeaderClasses: PropTypes.string,
  tabsAriaLabel: PropTypes.string,
  translationPath: PropTypes.string,
  parentTranslationPath: PropTypes.string,
  variant: PropTypes.string,
  isHiddenByPermissions: PropTypes.bool,
  permissionsReducerInput: PropTypes.string,
  permissions: PropTypes.arrayOf(PropTypes.string),
  tabsContentRefInput: PropTypes.string,
  tabsContentClasses: PropTypes.string,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  totalCountInput: PropTypes.string,
  themeClasses: PropTypes.oneOf([
    "theme-solid",
    "theme-default",
    "theme-curved",
  ]),
  scrollButtons: PropTypes.oneOf(["auto", "desktop", "off", "on"]),
  iconOnly: PropTypes.bool,
  isWithLine: PropTypes.bool,
  maxIndex: PropTypes.number,
  minIndex: PropTypes.number,
  dynamicComponentProps: PropTypes.instanceOf(Object),
  tabIndicatorProps: PropTypes.oneOfType([
    PropTypes.elementType,
    PropTypes.node,
  ]),
  getBeforeTabsComponent: PropTypes.func,
  getAfterTabsComponent: PropTypes.func,
  componentInput: PropTypes.string,
  idRef: PropTypes.string,
  dynamicIdRef: PropTypes.string,
  withDynamicComponents: PropTypes.bool,
  isPrimary: PropTypes.bool,
  isDisabled: PropTypes.bool,
  customLabel: PropTypes.string,
};
TabsComponent.defaultProps = {
  hiddenTabIndexes: [],
  currentTab: 0,
  iconInput: undefined,
  labelInput: undefined,
  scrollButtons: undefined,
  totalCountInput: undefined,
  isHiddenByPermissions: false,
  wrapperClasses: "",
  tabsHeaderClasses: undefined,
  translationPath: "",
  parentTranslationPath: "",
  tabsAriaLabel: "tabs",
  variant: "scrollable",
  themeClasses: "theme-default",
  orientation: undefined, // 'vertical',undefined (for horizontal)
  iconOnly: false,
  isWithLine: false,
  maxIndex: undefined,
  minIndex: undefined,
  tabsContentRefInput: undefined,
  tabsContentClasses: undefined,
  dynamicComponentProps: undefined,
  tabIndicatorProps: undefined,
  getBeforeTabsComponent: undefined,
  getAfterTabsComponent: undefined,
  componentInput: "component",
  withDynamicComponents: undefined,
  permissionsReducerInput: "PermissionsReducer.permissions",
  permissions: undefined,
  idRef: "tabRef",
  dynamicIdRef: "dynamicComponentRef",
  isDisabled: false,
  isPrimary: false,
  customLabel: undefined,
};
